import { directoryInfo } from "@p0security/shared/types/policy/constants";
import { IdpGroup, IdpGroups } from "@p0security/shared/types/policy/types";
import {
  Alert,
  Form,
  FormItemProps,
  RefSelectProps,
  Select,
  Spacing,
  Spin,
} from "@p0security/ui";
import { standardFormItemCols } from "components/common/forms/styles";
import { compact } from "lodash";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useInstalledDirectories } from "store/resources/DirectoriesProvider";
import { spacing } from "styles/spacing";

import { buildIdpGroupId, parseIdpGroupId } from "../utils";
import { IdpGroupPreview } from "./previews/IdpGroupPreview";

type Props = {
  value: IdpGroups;
  onChange: (value: IdpGroups) => void;
  error?: string;
  setError?: (error?: string) => void;
  formItemProps?: FormItemProps;
};

const membershipOptions = [
  { label: "Include members in any of these groups", value: "keep" as const },
  {
    label: "Exclude members in any of these groups",
    value: "remove" as const,
  },
];

export const IdpGroupInput: React.FC<Props> = ({
  value,
  onChange,
  error,
  setError,
  formItemProps = standardFormItemCols,
}) => {
  const inputRef = useRef<RefSelectProps>(null);

  const { allAvailableGroups, availableGroups, isLoadingGroups } =
    useInstalledDirectories();

  useEffect(() => {
    if (isLoadingGroups) return;
    inputRef.current?.focus();
  }, [isLoadingGroups]);

  // clear errors on unmount
  useEffect(
    () => () => {
      if (setError) {
        setError();
      }
    },
    [setError]
  );

  useEffect(() => {
    if (!setError) return;

    if (value.groups.length === 0) {
      setError("Please select at least one group.");
    } else {
      setError("");
    }
  }, [setError, value.groups.length]);

  const handleGroupChange = useCallback(
    (groupIds: string[]) => {
      const groups: IdpGroup[] = groupIds.map((groupId) => {
        const group = parseIdpGroupId(groupId);
        // A group already on the policy may be missing from the list if its directory failed to list its groups
        const label =
          availableGroups[group.directory].groups[groupId]?.label ??
          value.groups.find((g) => buildIdpGroupId(g) === groupId)?.label ??
          group.id;
        return { ...group, label };
      });
      onChange({
        type: "group",
        groups,
        effect: value.effect,
      });
    },
    [onChange, availableGroups, value.effect, value.groups]
  );

  const handleMembershipChange = useCallback(
    (effect: "keep" | "remove") => {
      onChange({
        type: "group",
        groups: value.groups,
        effect,
      });
    },
    [onChange, value.groups]
  );

  const groupValue = useMemo(
    () =>
      compact(
        value.groups.map((g) => (g.id === "" ? undefined : buildIdpGroupId(g)))
      ),
    [value]
  );

  const groupOptions = useMemo(
    () =>
      allAvailableGroups.map((ag) => ({
        label: <IdpGroupPreview idpGroup={ag} showPopover={false} />,
        group: ag,
        value: buildIdpGroupId(ag),
      })),
    [allAvailableGroups]
  );

  const groupSearch = useCallback(
    (term: string, option: (typeof groupOptions)[number] | undefined) =>
      term.length >= 3 &&
      !!option?.group.label.toLowerCase().includes(term.toLowerCase()),
    []
  );

  const invalidGroupIdWarning = useMemo(() => {
    if (value) {
      const { groups } = value;
      // Find one group that is not in allAvailableGroups
      // We don't have to find all since this validation runs on each group added
      const warningGroup = groups.find(
        (group) =>
          group.id && !allAvailableGroups.some((g) => group.id === g.id)
      );
      if (warningGroup) {
        const directoryLabel = directoryInfo[warningGroup.directory].label;

        return (
          <Alert
            message="Invalid group"
            description={
              <>
                A group with ID <code>{warningGroup.id}</code> could not be
                found in {directoryLabel}. It may have been deleted in{" "}
                {directoryLabel}. Please check that the group exists, or select
                a new group.
              </>
            }
            type="warning"
            showIcon
          />
        );
      }
    }

    return undefined;
  }, [value, allAvailableGroups]);

  const handleBlur = useCallback(() => {
    if (!setError) return;

    if (
      !value ||
      value.groups.length === 0 ||
      value.groups.some((g) => g.id === "")
    ) {
      setError("Please select at least one group.");
    } else {
      setError("");
    }
  }, [setError, value]);

  return isLoadingGroups ? (
    <div>
      <Spin tip="Loading groups..." />
    </div>
  ) : (
    <Form.Item
      {...formItemProps}
      label="Group"
      validateStatus={error ? "error" : ""}
      help={error}
    >
      <Select
        options={membershipOptions}
        value={value.effect}
        onChange={handleMembershipChange}
        showSearch={false}
        placeholder="Select membership"
        allowClear
      />
      <Spacing asChild mt={spacing.scale.xs}>
        <Select
          options={groupOptions}
          value={groupValue}
          onChange={handleGroupChange}
          showSearch
          filterOption={groupSearch}
          onBlur={handleBlur}
          placeholder="Select groups"
          mode="multiple"
          allowClear
          ref={inputRef}
        />
      </Spacing>
      {invalidGroupIdWarning && (
        <Spacing mt={spacing.scale.xs}>{invalidGroupIdWarning}</Spacing>
      )}
    </Form.Item>
  );
};
